import React from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { PushPin } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const MessageListMobile = ({
  messages,
  isLoading,
  onMessageClick,
  page,
  totalPages,
  onPageChange,
  totalElements,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " " + date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={6}
      >
        <Typography
          variant="body2"
          sx={{
            color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
            opacity: 0.6,
          }}
        >
          {t("messaging.nomessages", "No messages")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {messages.map((msg, index) => (
        <React.Fragment key={msg.id}>
          <Box
            onClick={() => onMessageClick(msg.id)}
            sx={{
              px: 2,
              py: 1.5,
              cursor: "pointer",
              backgroundColor: msg.isRead
                ? "transparent"
                : `${theme.palette.primary.main}08`,
              "&:active": {
                backgroundColor: `${theme.palette.primary.main}15`,
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box flex={1} mr={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
                    fontWeight: msg.isRead ? 400 : 700,
                    fontSize: "13px",
                    lineHeight: 1.4,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {msg.isPinned && (
                    <PushPin
                      sx={{
                        fontSize: 14,
                        color: theme.palette.primary.main,
                      }}
                    />
                  )}
                  {msg.subject}
                </Typography>
                {msg.preview && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
                      opacity: 0.6,
                      fontSize: "11px",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mt: 0.3,
                    }}
                  >
                    {msg.preview}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
                  opacity: 0.5,
                  fontSize: "10px",
                  whiteSpace: "nowrap",
                  mt: 0.3,
                }}
              >
                {formatDate(msg.date)}
              </Typography>
            </Box>
          </Box>
          {index < messages.length - 1 && (
            <Divider
              sx={{
                borderColor: theme.palette.divider1 || "#444",
                mx: 2,
              }}
            />
          )}
        </React.Fragment>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
          <Typography
            variant="caption"
            sx={{
              color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
              opacity: 0.6,
              mb: 1,
            }}
          >
            {`Showing ${(page - 1) * 10 + 1} - ${Math.min(page * 10, totalElements)} of ${totalElements} items.`}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                color: process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white",
              },
              "& .Mui-selected": {
                backgroundColor: `${theme.palette.primary.main} !important`,
                color: "white",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MessageListMobile;
